import { useMemo, useState } from "react";
import { X, ListChecks, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { TouchTooltip } from "@/components/ui/touch-tooltip";
import { cn } from "@/lib/utils";
import type { SelectedService } from "@/types/service";

type SelectedServicesSummaryProps = {
  selectedServices: SelectedService[];
  onRemove?: (service: SelectedService) => void;
  collapsible?: boolean;
};

function SelectedServicesSummary({
  selectedServices,
  onRemove,
  collapsible = false,
}: SelectedServicesSummaryProps) {
  const [open, setOpen] = useState(true);

  const groupedBySector = useMemo(() => {
    const groups = new Map<string, SelectedService[]>();
    for (const service of selectedServices) {
      const existing = groups.get(service.sector_name) ?? [];
      existing.push(service);
      groups.set(service.sector_name, existing);
    }
    return groups;
  }, [selectedServices]);

  if (selectedServices.length === 0) return null;

  const header = (
    <div
      className={cn(
        "flex items-center gap-2 mb-3",
        collapsible && "cursor-pointer hover:opacity-80 transition-opacity mb-0"
      )}
    >
      <ListChecks className="w-4 h-4 text-ds-blue-600" />
      <span className="text-sm font-semibold text-ds-text-600">
        Servicii selectate
      </span>
      <Badge
        variant="outline"
        className={cn(
          "text-[10px] tabular-nums bg-white",
          collapsible ? "ml-1" : "ml-auto"
        )}
      >
        {selectedServices.length}
      </Badge>
      {collapsible && (
        <ChevronDown
          className={cn(
            "w-4 h-4 ml-auto text-ds-text-400 transition-transform",
            open && "rotate-180"
          )}
        />
      )}
    </div>
  );

  const servicesList = (
    <div className={cn("space-y-3", collapsible && "pt-3")}>
      {Array.from(groupedBySector.entries()).map(([sectorName, services]) => (
        <div key={sectorName}>
          <p className="text-[10px] font-semibold text-ds-text-400 uppercase tracking-wider mb-1.5">
            {sectorName}
            <span className="ml-1.5 text-ds-text-300 normal-case tracking-normal">
              ({services.length})
            </span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {services.map((service) => (
              <TouchTooltip
                key={`${service.category_id}-${service.name}`}
                content={service.name}
                side="top"
              >
                <div
                  className={cn(
                    "group flex items-center gap-1.5 bg-white border rounded-lg pl-2 pr-1 py-1",
                    "border-ds-border-200 hover:border-ds-border-300 transition-colors duration-150"
                  )}
                >
                  <span
                    className={cn(
                      "w-1.5 h-1.5 rounded-full shrink-0",
                      service.category_id === "1"
                        ? "bg-ds-blue-600"
                        : "bg-ds-text-400"
                    )}
                  />
                  <span className="text-xs text-ds-text-600 max-w-[200px] truncate">
                    {service.name}
                  </span>
                  {onRemove && (
                    <button
                      type="button"
                      onClick={() => onRemove(service)}
                      className="shrink-0 w-5 h-5 rounded-md flex items-center justify-center text-ds-text-300 hover:text-ds-red-500 hover:bg-ds-red-500/5 transition-colors duration-150"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </TouchTooltip>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  if (!collapsible) {
    return (
      <div className="mt-5 rounded-xl border border-ds-border-200/80 bg-gradient-to-b from-ds-surface-50/90 to-ds-surface-100/60 p-4 shadow-sm">
        {header}
        {servicesList}
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="mt-5 rounded-xl border border-ds-border-200/80 bg-gradient-to-b from-ds-surface-50/90 to-ds-surface-100/60 p-4 shadow-sm">
        <CollapsibleTrigger asChild>{header}</CollapsibleTrigger>
        <CollapsibleContent>{servicesList}</CollapsibleContent>
      </div>
    </Collapsible>
  );
}

export default SelectedServicesSummary;
